// schemas
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
	COMPANY_DOCS_SIZE,
	DOWNLOAD_ALL_MY_FILES,
	DocumentTableRow,
	DocumentVisibility,
	DocumentsData,
	FILE,
	FOLDER,
	TOTAL_SIZE,
} from "./schema";

// function to convert company fize size to GB
export function formatCompanyTotalFileSize(maxDocSpace) {
	const sizes = ["MB", "GB", "TB"];

	for (let i = 0; i < sizes.length; i++) {
		const size = sizes[i];
		const s = maxDocSpace / Math.pow(1024, i);

		if (s < 1024) {
			return `${s.toFixed(1)} ${size}`;
		}
	}

	return `${maxDocSpace} MB`; // Default, in case the loop doesn't return a value
}

// function to convert bytes to KB
export function bytesToKB(bytes) {
	if (bytes === 0) return "0 KB";

	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// format row for TableLite, add document folder then its files, total size of all files and download all my files
export function buildRows(buildRowsProps): DocumentTableRow[] {
	const rows: DocumentTableRow[] = [];
	// states
	const {
		documents,
		folderState,
		toggleFolderState,
		userData,
		isCompanyDocumentPage,
		embeddingTasks,
		isEmbeddingLimitReached,
	} = buildRowsProps;
	const { companyDocumentsOpen, signedDocumentsOpen, myDocumentsOpen } = folderState;
	const { employeeID, thisUserId, employeeName } = userData;

	// functions
	const { handleSelectEdit, handleSelectDelete, handleViewDocument, handleToggleAiRetrieval } = buildRowsProps;

	let totalSize = 0; // total size of all files
	const companyFilesTotalSize = documents?.companyFiles?.reduce((acc, f) => acc + f.length, 0) || 0;

	// Company Documents
	rows.push({
		type: FOLDER,
		id: "companyDocumentsOpen",
		name: "Company Documents",
		isOpen: companyDocumentsOpen,
		toggleFolderState,
	});
	documents &&
		companyDocumentsOpen &&
		documents.companyFiles?.forEach((f) => {
			const taskState = embeddingTasks?.get(f._id);
			rows.push({
				type: FILE,
				...f,
				id: f._id,
				handleSelectEdit,
				handleSelectDelete,
				handleViewDocument,
				handleToggleAiRetrieval,
				userData,
				isCompanyDocumentPage,
				isProcessing: !!taskState,
				isEmbeddingLimitReached,
				embeddingProgress: taskState?.progress,
			});
			totalSize += f.length;
		});

	// Signed Files
	rows.push({
		type: FOLDER,
		id: "signedDocumentsOpen",
		name: "Signed Documents",
		isOpen: signedDocumentsOpen,
		toggleFolderState,
	});
	documents &&
		signedDocumentsOpen &&
		documents.signedFiles?.forEach((f) => {
			rows.push({
				type: FILE,
				...f,
				id: f._id,
				handleSelectEdit,
				handleSelectDelete,
				handleViewDocument,
				handleToggleAiRetrieval,
				userData,
				isCompanyDocumentPage,
				isProcessing: embeddingTasks?.has(f._id) ?? false,
			});
			totalSize += f.length;
		});

	// My Documents
	employeeName &&
		rows.push({
			type: FOLDER,
			id: "myDocumentsOpen",
			name: `${thisUserId === employeeID ? "My" : employeeName + "'s"} Documents`,
			isOpen: myDocumentsOpen,
			toggleFolderState,
		});
	documents &&
		myDocumentsOpen &&
		documents.myFiles?.forEach((f) => {
			rows.push({
				type: FILE,
				...f,
				id: f._id,
				handleSelectEdit,
				handleSelectDelete,
				handleViewDocument,
				handleToggleAiRetrieval,
				userData,
				isCompanyDocumentPage,
				isProcessing: embeddingTasks?.has(f._id) ?? false,
			});
			totalSize += f.length;
		});
	documents &&
		myDocumentsOpen &&
		documents.myFiles?.length > 0 &&
		rows.push({ id: DOWNLOAD_ALL_MY_FILES, name: "Download All", type: DOWNLOAD_ALL_MY_FILES, userId: employeeID });

	// Company Documents
	if (isCompanyDocumentPage) {
		rows.push({
			type: TOTAL_SIZE,
			id: COMPANY_DOCS_SIZE,
			name: "Company Documents: ",
			totalSize: companyFilesTotalSize,
		});
		rows.push({
			type: TOTAL_SIZE,
			id: TOTAL_SIZE,
			name: "Total (including individual user's documents): ",
			userId: employeeID,
			totalSize: documents.companyTotalFilesSize,
			companyMaxSpace: documents.companyMaxSpace,
		});
	} else {
		// Total size
		rows.push({ type: TOTAL_SIZE, id: TOTAL_SIZE, name: "Total Size: ", userId: employeeID, totalSize });
	}

	return rows;
}

// return default options for visibility when uploading a document or editing a document
export const getVisibilityOptions = (isCompanyDocumentPage: boolean, employee: string): DocumentVisibility[] => {
	if (isCompanyDocumentPage) {
		return [
			{ value: "vis_to_everyone", label: "Everyone" },
			{ value: "vis_to_man", label: "Administrators Only" },
		];
	} else {
		return [
			{ value: "vis_to_emp", label: employee },
			{ value: "vis_to_m", label: "Management" },
		];
	}
};

// intentional since we render items based on this data conditionally
export const useIsCompanyDocumentPage = () => {
	const location = useLocation();

	const isDocumentPage =
		location.pathname.includes("company/documents") || location.pathname.includes("company/resources");
	const [isCompanyDocumentPage, setIsCompanyDocumentPage] = useState(isDocumentPage);

	useEffect(() => {
		setIsCompanyDocumentPage(isDocumentPage);
	}, [location.pathname]);

	return isCompanyDocumentPage;
};

export const filterDocuments = (documents: DocumentsData, tag: string) => {
	const docKeys = new Set(["myFiles", "companyFiles", "signedFiles"]); // key in documents that has DocumentFile value
	return Object.keys(documents).reduce((acc, key) => {
		if (docKeys.has(key)) {
			acc[key] =
				documents[key]?.filter((doc) =>
					doc.tags?.some((val) => val.toLowerCase().includes(tag.toLocaleLowerCase()))
				) || [];
		} else {
			acc[key] = documents[key];
		}
		return acc;
	}, {}) as DocumentsData;
};

export function isImageFile(fileName: string) {
	if (!fileName) return false;
	const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg", ".webp"];
	// Extract the file extension from file name
	const fileExtension = fileName.split(".").pop()?.toLowerCase();
	return imageExtensions.includes("." + fileExtension);
}

/**
 * Extracts the error type from a full error message.
 * Handles both full error strings from SSE and page reload.
 *
 * @param fullError - Full error string like "PDFValidationError-size: PDF size (75.32MB) exceeds..."
 * @returns Error type like "PDFValidationError-size" or undefined
 *
 * @example
 * extractValidationErrorType("PDFValidationError-size: PDF size exceeds limit")
 * // => "PDFValidationError-size"
 *
 * extractValidationErrorType("PDFValidationError-pageCount: Too many pages")
 * // => "PDFValidationError-pageCount"
 */
export function extractValidationErrorType(fullError: string | undefined): string | undefined {
	if (!fullError) return undefined;

	// Extract type from "PDFValidationError-size: message..." format
	if (fullError.startsWith("PDFValidationError-")) {
		return fullError.split(":", 1)[0];
	}

	return fullError;
}

/**
 * Maps validation error types to user-friendly messages.
 *
 * @param errorType - Error type like "PDFValidationError-size"
 * @returns User-friendly error message
 *
 * @example
 * getFailureMessage("PDFValidationError-size")
 * // => "Failed to process document. Document must be under 50MB."
 */
export function getFailureMessage(errorType: string | undefined): string {
	switch (errorType) {
		case "PDFValidationError-size":
			return "Failed to process document. Document must be under 50MB.";
		case "PDFValidationError-invalid":
			return "Failed to process document. Document must be a valid PDF.";
		case "PDFValidationError-pageCount":
			return "Failed to process document. Document must be under 2000 pages.";
		default:
			return "Failed to process document for use in Ask PerformYard.";
	}
}
